import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Home, LogOut, User, Clock, Calendar, Menu, X } from "lucide-react";

import { OverviewTab } from "../components/portal/OverviewTab";
import { HoursTab } from "../components/portal/HoursTab";
import { EventsTab } from "../components/portal/EventsTab";
import { ProfileTab } from "../components/portal/ProfileTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, loading, logout, updateProfile } = useUserAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [hourLogs, setHourLogs] = useState(null);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [hourForm, setHourForm] = useState({ date: "", hours: "", activity_type: "volunteer", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/dashboard`, { withCredentials: true });
      setDashboard(res.data);
    } catch {
      // silently fail - dashboard will show loading state
    }
  }, []);

  const fetchHourLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/hours`, { withCredentials: true });
      setHourLogs(res.data);
    } catch {
      toast.error("Failed to load hour logs");
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch {
      // silently fail
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { withCredentials: true });
      setAnnouncements(res.data);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/portal/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchDashboard();
      fetchEvents();
      fetchAnnouncements();
      setProfileForm({ name: user.name || "", phone: user.phone || "" });
    }
  }, [user, fetchDashboard, fetchEvents, fetchAnnouncements]);

  useEffect(() => {
    if (user && activeTab === "hours") fetchHourLogs();
  }, [user, activeTab, fetchHourLogs]);

  const handleLogout = async () => {
    await logout();
    navigate("/portal/login");
    toast.success("Logged out");
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(profileForm);
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const handleLogHours = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/user/hours`, {
        ...hourForm,
        hours: parseFloat(hourForm.hours),
      }, { withCredentials: true });
      toast.success("Hours logged successfully");
      setHourForm({ date: "", hours: "", activity_type: "volunteer", description: "" });
      fetchHourLogs();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log hours");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Delete this hour log?")) return;
    try {
      await axios.delete(`${API}/user/hours/${logId}`, { withCredentials: true });
      toast.success("Deleted");
      fetchHourLogs();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-warm-700 border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: Home },
    { id: "hours", label: "Log Hours", icon: Clock },
    { id: "events", label: "Events", icon: Calendar },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Logo" className="w-10 h-10 object-contain" />
              <span className="font-semibold text-gray-900 text-sm" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>My Portal</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="mt-2 mb-6 px-3 py-2.5 bg-warm-50 rounded-xl">
            <p className="font-medium text-sm text-gray-900 truncate">{user.name}</p>
            <p className="text-xs text-warm-700 capitalize">{user.role}</p>
          </div>

          <nav className="space-y-1 flex-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                data-testid={`portal-tab-${tab.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  activeTab === tab.id ? "bg-warm-50 text-warm-700" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="space-y-2 mt-auto">
            <Link to="/">
              <Button variant="outline" className="w-full justify-start gap-2 rounded-xl text-sm">
                <Home className="w-4 h-4" />View Website
              </Button>
            </Link>
            <Button variant="ghost" onClick={handleLogout} data-testid="portal-logout" className="w-full justify-start gap-2 text-gray-600 hover:text-red-600 rounded-xl text-sm">
              <LogOut className="w-4 h-4" />Logout
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
          <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
            {tabs.find(t => t.id === activeTab)?.label}
          </h1>
        </header>

        <div className="p-6">
          {activeTab === "overview" && dashboard && (
            <OverviewTab dashboard={dashboard} announcements={announcements} />
          )}
          {activeTab === "hours" && (
            <HoursTab
              hourLogs={hourLogs}
              hourForm={hourForm}
              setHourForm={setHourForm}
              isSubmitting={isSubmitting}
              onLogHours={handleLogHours}
              onDeleteLog={handleDeleteLog}
            />
          )}
          {activeTab === "events" && <EventsTab events={events} />}
          {activeTab === "profile" && (
            <ProfileTab
              user={user}
              profileForm={profileForm}
              setProfileForm={setProfileForm}
              onSubmit={handleProfileUpdate}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;
